/**
 * Country Labels Layer Management
 *
 * Provides toggle control for displaying country names on the map.
 * Similar to the Mapbox attribution control, hidden until accessed.
 */

import type mapboxgl from "mapbox-gl";

/**
 * Add a country labels layer to the map
 * Labels are initially hidden and can be toggled by the control
 *
 * @param map - Mapbox GL map instance
 */
export function addCountryLabels(map: mapboxgl.Map): void {
  // Check if layer already exists
  if (map.getLayer("country-labels")) {
    return;
  }

  // Use Mapbox's composite source which includes natural-earth labels
  // This is more reliable than trying to add symbols to country-boundaries-v1
  try {
    // First, add the composite source if it doesn't exist
    if (!map.getSource("composite")) {
      map.addSource("composite", {
        type: "vector",
        url: "mapbox://mapbox.mapbox-streets-v8",
      });
    }

    map.addLayer({
      id: "country-labels",
      type: "symbol",
      source: "composite",
      "source-layer": "place_label", // This layer contains country labels
      filter: ["==", ["get", "type"], "country"], // Only show countries
      layout: {
        "text-field": ["get", "name"], // Country name (in native script)
        "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
        "text-size": 14,
        "text-transform": "uppercase",
        "text-letter-spacing": 0.15,
        // Initially hidden
        visibility: "none",
      },
      paint: {
        "text-color": "#F3B33B", // Accent color
        "text-halo-color": "#000000",
        "text-halo-width": 0,
        "text-halo-blur": 1,
        "text-opacity": 1,
      },
    });
  } catch (error) {
    console.error("[countryLabels] Error creating layer:", error);
  }
} /**
 * Toggle country labels visibility
 *
 * @param map - Mapbox GL map instance
 * @param visible - true to show labels, false to hide
 */
export function toggleCountryLabels(map: mapboxgl.Map, visible: boolean): void {
  const layer = map.getLayer("country-labels");

  if (layer) {
    map.setLayoutProperty("country-labels", "visibility", visible ? "visible" : "none");
  }
}

/**
 * Create a toggle control for country labels
 * Styled similar to Mapbox's attribution control
 *
 * @param map - Mapbox GL map instance
 * @returns Cleanup function to remove the control
 */
export function createCountryLabelsControl(map: mapboxgl.Map): () => void {
  // Create control container
  const controlContainer = document.createElement("div");
  controlContainer.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
  controlContainer.style.cssText = `
        margin: 0 0 10px 10px;
    `;

  // Create button
  const button = document.createElement("button");
  button.className = "mapboxgl-ctrl-icon";
  button.type = "button";
  button.setAttribute("aria-label", "Toggle country names");
  button.title = "Toggle country names";
  button.style.cssText = `
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-image: none;
        background-color: transparent;
        font-size: 12px;
        font-weight: 600;
        color: #333;
        cursor: pointer;
    `;
  button.innerHTML = "Aa";

  // Track state
  let labelsVisible = false;

  // Click handler
  button.addEventListener("click", () => {
    labelsVisible = !labelsVisible;
    toggleCountryLabels(map, labelsVisible);

    // Update button appearance
    if (labelsVisible) {
      button.style.backgroundColor = "#F3B33B";
      button.style.color = "#000";
    } else {
      button.style.backgroundColor = "transparent";
      button.style.color = "#333";
    }
  });

  controlContainer.appendChild(button);

  // Add to map container (bottom-left corner)
  const mapContainer = map.getContainer();

  // Check if bottom-left control container already exists
  let bottomLeft = mapContainer.querySelector(".mapboxgl-ctrl-bottom-left") as HTMLDivElement;

  if (!bottomLeft) {
    // Create bottom-left container if it doesn't exist
    bottomLeft = document.createElement("div");
    bottomLeft.className = "mapboxgl-ctrl-bottom-left";
    bottomLeft.style.cssText = `
            position: absolute;
            bottom: 0;
            left: 0;
            display: flex;
            flex-direction: column;
            pointer-events: none;
        `;
    mapContainer.appendChild(bottomLeft);
  }

  // Add our control to the bottom-left container
  bottomLeft.style.pointerEvents = "auto";
  bottomLeft.appendChild(controlContainer);

  // Cleanup function
  return () => {
    controlContainer.remove();
    // Remove bottom-left container only if it's now empty
    if (bottomLeft?.children.length === 0) {
      bottomLeft.remove();
    }
  };
}
